import { useStore } from "@/store/store";
import { computed } from "vue";

/**
 * Composable for managing team leader contact actions.
 * @returns {Object} Reactive state and methods for contact actions.
 */
export function useTeamLeaderContactActions() {
  const store = useStore();

  // Computed properties to get data from the Vuex store
  const actions = computed(() => store.state.dashboardMyContacts.teamLeaderContactActions);
  const storeLoading = computed({
    get() {
      return store.state.dashboardMyContacts.teamLeaderContactActionsLoading;
    },
    set(value) {
      store.commit("dashboardMyContacts/setTeamLeaderContactActionsLoading", value);
    },
  });
  const storeError = computed({
    get() {
      return store.state.dashboardMyContacts.teamLeaderContactActionsError;
    },
    set(value) {
      store.commit("dashboardMyContacts/setTeamLeaderContactActionsError", value);
    },
  });
  const storePagination = computed({
    get() {
      return store.state.dashboardMyContacts.teamLeaderContactActionsPagination;
    },
    set(value) {
      store.commit("dashboardMyContacts/setTeamLeaderContactActionsPagination", value);
    },
  });
  const storeLastFetchParams = computed({
    get() {
      return store.state.dashboardMyContacts.teamLeaderContactActionsLastFetchParams;
    },
    set(value) {
      store.commit("dashboardMyContacts/setTeamLeaderContactActionsLastFetchParams", value);
    },
  });

  /**
   * Fetches actions for the current contact ID from the store.
   * @param {String|Number} contactId - The ID of the contact to fetch actions for.
   * @param {Object} [params={}] - Additional parameters for the API request (e.g., pagination).
   */
  const fetchContactActions = async (contactId, params = {}) => {
    if (!contactId) {
      storeError.value = "Contact ID is required to fetch actions.";
      return;
    }

    if (params && Object.keys(params).length > 0) {
      storeLastFetchParams.value = params;
    }

    storeLoading.value = true;
    storeError.value = null;
    try {
      const response = await store.dispatch("dashboardMyContacts/fetchTeamLeaderContactActions", {
        contactId,
        params: {
          page: storePagination.value.page,
          per_page: storePagination.value.per_page,
          ...storeLastFetchParams.value,
        },
      });
      if (response && response.meta) {
        storePagination.value = {
          page: response.meta.page,
          per_page: response.meta.per_page,
          total: response.meta.total,
        };
      }
    } catch (err) {
      const errors = err.errors.map((error) => error.message).join("; ");
      storeError.value =
        err.errors.length > 0
          ? `${err.message}${errors ? `: ${errors}` : ""}`
          : "Failed to fetch contact actions";
    } finally {
      storeLoading.value = false;
    }
  };

  /**
   * Creates a new action for the current contact ID.
   * @param {String|Number} contactId - The ID of the contact.
   * @param {Object} actionData - The data for the new action.
   * @throws {Error} If contact ID is not set.
   * @returns {Promise<Object>} The created action data.
   */
  const createContactAction = async (contactId, actionData) => {
    if (!contactId) {
      storeError.value = "Contact ID is required to create an action.";
      throw new Error(storeError.value);
    }
    storeLoading.value = true;
    storeError.value = null;
    try {
      const newAction = await store.dispatch("dashboardMyContacts/createTeamLeaderContactAction", {
        contactId,
        actionData,
      });
      return newAction;
    } catch (err) {
      const errors = err.errors.map((error) => error.message).join("; ");
      storeError.value =
        err.errors.length > 0
          ? `${err.message}${errors ? `: ${errors}` : ""}`
          : "Failed to create contact action";
      throw err;
    } finally {
      storeLoading.value = false;
    }
  };

  return {
    actions,
    storeLoading,
    storeError,
    storePagination,
    fetchContactActions,
    createContactAction,
  };
}
